/**
 * Pipeline CARD TAGS — the taxonomy every talent surface shows, owned once.
 *
 * ─── Why the union lives here and not in the portal ────────────────────────
 *
 * A card wears a chip that says where its person really is: "Draft", "Awaiting
 * acceptance", "Vetting", "Mobility", "Adaptation", "Care", "Sponsored". Until
 * W7 that chip was DERIVED IN THE COMPONENT — `cardTagsOf(talent)` in
 * business-web re-read the lifecycle step for the invitation and vetting
 * phases, and for the sponsored phase ignored the step entirely and re-ORed
 * three booleans through a two-level fallback:
 *
 *   if (detail?.mobilityActive ?? talent.mobilityActive) heroes.push('mobility');
 *
 * The server already emits distinct sponsored step ids (`sponsored_mobility`,
 * `sponsored_adaptation`, `sponsored_care`, `sponsored_evidence`,
 * `sponsored_pending`) and carries the three hero flags on the step's `detail`
 * precisely so the plural truth travels. Two sources, two readers, one card —
 * so a card could show "Care" while its own step said `sponsored_mobility`.
 * That is rule 9: a badge is not a rendering decision.
 *
 * So the ANSWER moves server-side (`computeCardTags`, beside the lifecycle
 * step) and this package owns the vocabulary both halves speak. The front end
 * keeps exactly two things, and they are the two things a backend must not
 * own: the display metadata (label key, icon) and the pure predicates over
 * untrusted URL input (`isPipelineCardTag`, `matchesCardTags`).
 *
 * ─── Why an ARRAY ──────────────────────────────────────────────────────────
 *
 * A sponsored talent can be live in Mobility, Adaptation and Care at once, and
 * the board renders one chip per active hero. Tags are therefore plural for
 * sponsored rows and singular everywhere else.
 */

// ═══════════════════════════════════════════════════════════════════════
// BUCKETS — the three kanban columns
// ═══════════════════════════════════════════════════════════════════════

/** The three kanban columns. The tag axis is a refinement of this one. */
export type PipelineBucket = 'acquisition' | 'vetting' | 'sponsored';

export const BUCKETS = [
  'acquisition',
  'vetting',
  'sponsored',
] as const satisfies readonly PipelineBucket[];

/**
 * Column for a canonical stage. Sponsored is the default because the sponsored
 * PHASE is expressed as its sub-stages (mobility / integration / care /
 * evidence) on `journey_instances.pipeline_stage`.
 */
export function toBucket(stage: string | null | undefined): PipelineBucket {
  if (stage === 'acquisition') return 'acquisition';
  if (stage === 'vetting') return 'vetting';
  return 'sponsored';
}

// ═══════════════════════════════════════════════════════════════════════
// THE UNION
// ═══════════════════════════════════════════════════════════════════════

export type PipelineCardTag =
  | 'draft'
  | 'awaiting_acceptance'
  | 'signed_up_not_accepted'
  | 'invite_declined'
  | 'invite_expired'
  | 'invite_problem'
  | 'acquisition'
  | 'onboarding_incomplete'
  | 'waiting_for_checkup'
  | 'checkup_in_progress'
  | 'awaiting_score'
  | 'checkup_withheld'
  | 'consent_withdrawn'
  | 'awaiting_review'
  | 'testit_pending'
  | 'testit_complete'
  | 'testit_passed'
  | 'testit_declined'
  | 'sponsorship_offered'
  | 'vetting'
  | 'mobility'
  | 'adaptation'
  | 'care'
  | 'sponsored';

/** Journey order — the order the filter renders them in. */
export const PIPELINE_CARD_TAGS = [
  'draft',
  'awaiting_acceptance',
  'signed_up_not_accepted',
  'invite_declined',
  'invite_expired',
  'invite_problem',
  'acquisition',
  'onboarding_incomplete',
  'waiting_for_checkup',
  'checkup_in_progress',
  'awaiting_score',
  'checkup_withheld',
  'consent_withdrawn',
  'awaiting_review',
  'testit_pending',
  'testit_complete',
  'testit_passed',
  'testit_declined',
  'sponsorship_offered',
  'vetting',
  'mobility',
  'adaptation',
  'care',
  'sponsored',
] as const satisfies readonly PipelineCardTag[];

/**
 * Compile-time completeness: adding a value to the union without listing it
 * above fails to typecheck. The same guard `LIFECYCLE_STEP_IDS` carries, for
 * the same reason — a runtime length check only fails once someone runs it.
 */
type _PipelineCardTagsAreExhaustive =
  Exclude<PipelineCardTag, (typeof PIPELINE_CARD_TAGS)[number]> extends never ? true : never;
/** Reads the guard so the compiler evaluates it; never used at runtime. */
export const PIPELINE_CARD_TAGS_ARE_EXHAUSTIVE: _PipelineCardTagsAreExhaustive = true;

/**
 * Which column each tag belongs to.
 *
 * The invariant this file exists to protect is "a tag's declared bucket
 * matches the bucket its cards land in". It is stated here, once, so the
 * producer (`computeCardTags`) and the consumer (`tagsForBucket`, the chip
 * renderer) cannot disagree about it.
 */
export const PIPELINE_CARD_TAG_BUCKET: Record<PipelineCardTag, PipelineBucket> = {
  draft: 'acquisition',
  awaiting_acceptance: 'acquisition',
  signed_up_not_accepted: 'acquisition',
  invite_declined: 'acquisition',
  invite_expired: 'acquisition',
  invite_problem: 'acquisition',
  acquisition: 'acquisition',
  onboarding_incomplete: 'vetting',
  waiting_for_checkup: 'vetting',
  checkup_in_progress: 'vetting',
  awaiting_score: 'vetting',
  checkup_withheld: 'vetting',
  consent_withdrawn: 'vetting',
  awaiting_review: 'vetting',
  testit_pending: 'vetting',
  testit_complete: 'vetting',
  testit_passed: 'vetting',
  testit_declined: 'vetting',
  sponsorship_offered: 'vetting',
  vetting: 'vetting',
  mobility: 'sponsored',
  adaptation: 'sponsored',
  care: 'sponsored',
  sponsored: 'sponsored',
};

/**
 * The tags that make up a column.
 *
 * Used to translate the legacy `?bucket=` deep links — Cohorts, Programs and
 * the Home acquisition card all still emit them, and a bookmark from before
 * the tag filter existed must keep working rather than land on an unfiltered
 * board.
 */
export function tagsForBucket(bucket: PipelineBucket): PipelineCardTag[] {
  return PIPELINE_CARD_TAGS.filter((tag) => PIPELINE_CARD_TAG_BUCKET[tag] === bucket);
}

/** Narrowing guard for untrusted input (URL params). */
export function isPipelineCardTag(value: unknown): value is PipelineCardTag {
  return typeof value === 'string' && (PIPELINE_CARD_TAGS as readonly string[]).includes(value);
}
